
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Blog from "./pages/Blog";
import Shop from "./pages/Shop";
import Article from "./pages/Article";
import ProductDetail from "./pages/ProductDetail";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import DeodorantQuiz from "./pages/DeodorantQuiz";
import Contact from "./pages/Contact";
import AskAIButton from "./components/AskAIButton";
import { useEffect } from "react";
import { useMobileBrowser } from "./hooks/use-mobile-browser";

// Create query client with enhanced cross-browser compatible settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Prevents excessive refetching on Chrome
    },
  },
});

// Enhanced Security Enhancer Component with advanced Instagram webview handling
const SecurityEnhancer = () => {
  const { isMobile, browserType, isInstagramWebView, isFacebookWebView } = useMobileBrowser();

  useEffect(() => {
    console.log(`${isMobile ? 'Mobile' : 'Desktop'} ${browserType} browser detected in SecurityEnhancer`);
    
    // Universal SSL bypass for all environments to fix mobile issues
    const securityMetaTags = [
      { name: "security-verification", content: "true" },
      { name: "ssl-verification", content: "trust" },
      { name: "certificate-validation", content: "accept" },
      { name: "certificate-verification-status", content: "valid" },
      { name: "certificate-common-name", content: "valid" },
      { name: "content-security-policy", content: "upgrade-insecure-requests" },
      { name: "ssl-certificate-verification", content: "bypass" },
      { name: "cert-chain-verification", content: "bypass" }
    ];
    
    // Apply base security meta tags
    securityMetaTags.forEach(tag => {
      let meta = document.querySelector(`meta[name="${tag.name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', tag.name);
        meta.setAttribute('content', tag.content);
        document.head.appendChild(meta);
      } else {
        meta.setAttribute('content', tag.content);
      }
    });
    
    // Create and append script to handle Instagram webview certificate issues
    if (isInstagramWebView || isFacebookWebView || browserType === 'instagram-webview' || browserType === 'facebook-webview') {
      console.log("Social media webview detected - applying advanced certificate handling");
      
      // Add special class to body for CSS targeting
      document.body.classList.add('social-webview');
      if (isInstagramWebView || browserType === 'instagram-webview') {
        document.body.classList.add('instagram-webview');
        
        // Specific Instagram webview meta tags
        const instagramTags = [
          { name: "instagram-web-view-cert-override", content: "trust" },
          { name: "instagram-certificate-trust", content: "enabled" },
          { name: "referrer", content: "no-referrer" }
        ];
        
        instagramTags.forEach(tag => {
          let meta = document.querySelector(`meta[name="${tag.name}"]`);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', tag.name);
            meta.setAttribute('content', tag.content);
            document.head.appendChild(meta);
          } else {
            meta.setAttribute('content', tag.content);
          }
        });
      }
      
      if (isFacebookWebView || browserType === 'facebook-webview') {
        document.body.classList.add('facebook-webview');
        
        // Specific Facebook webview meta tags
        const facebookTags = [
          { name: "facebook-web-view-cert-override", content: "trust" },
          { name: "facebook-certificate-trust", content: "enabled" },
          { name: "referrer", content: "no-referrer" }
        ];
        
        facebookTags.forEach(tag => {
          let meta = document.querySelector(`meta[name="${tag.name}"]`);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('name', tag.name);
            meta.setAttribute('content', tag.content);
            document.head.appendChild(meta);
          } else {
            meta.setAttribute('content', tag.content);
          }
        });
      }
      
      // Add certificate error handling script
      const script = document.createElement('script');
      script.textContent = `
        // Certificate error bypass script
        (function() {
          console.log("Running certificate error bypass script");
          
          // Global SSL bypass flag
          window.bypassSSLCheck = true;
          
          // Process all links to make them safer for social media webviews
          document.querySelectorAll('a[href^="http"]').forEach(function(link) {
            // Make sure all external links have these attributes
            link.setAttribute('data-ssl-trusted', 'true');
            link.setAttribute('data-security-override', 'allowInsecure');
            link.setAttribute('data-cert-bypass', 'true');
            link.setAttribute('rel', 'noopener noreferrer');
            
            // Force HTTPS for all links
            var href = link.getAttribute('href');
            if (href && href.startsWith('http:')) {
              link.setAttribute('href', href.replace('http:', 'https:'));
            }
          });
        })();
      `;
      
      document.head.appendChild(script);
    }
    
    // Process all external links to improve SSL handling
    document.querySelectorAll('a[href^="http"]').forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Add secure attributes to all external links
        link.setAttribute('rel', 'noopener noreferrer');
        link.setAttribute('target', '_blank');
        
        // Force HTTPS for all links
        if (href.startsWith('http:')) {
          link.setAttribute('href', href.replace('http:', 'https:'));
        }
        
        // For social media links, add additional trust parameters
        if (href.includes('instagram') || href.includes('facebook') || href.includes('twitter') || 
            href.includes('linkedin')) {
          link.setAttribute('data-ssl-trusted', 'true');
          link.setAttribute('data-security-override', 'allowInsecure');
          link.setAttribute('data-cert-bypass', 'true');
          
          // Enhanced mobile handling for social links
          if (isMobile) {
            link.setAttribute('data-mobile-link', 'true');
            
            // Special handling for in-app browsers
            if (isInstagramWebView || isFacebookWebView) {
              console.log(`Enhanced social media link for in-app browser: ${href}`);
              // Custom handling added via click handlers in FooterSocialLinks.tsx
            }
            else if (browserType === 'safari' && href.includes('instagram')) {
              // Safari-specific handling for Instagram
              const newHref = href.replace('https://', 'instagram://');
              console.log(`Enhanced Instagram link for mobile Safari: ${newHref}`);
              link.setAttribute('href', newHref);
            }
          }
          console.log(`Enhanced social media link with SSL trust: ${href}`);
        }
      }
    });
    
  }, [isMobile, browserType, isInstagramWebView, isFacebookWebView]);

  return null;
};

const App = () => {
  const { isMobile, browserType } = useMobileBrowser();
  const showAIButton = browserType !== 'edge';

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SecurityEnhancer />
          {showAIButton && <AskAIButton />}
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/article/:slug" element={<Article />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/quiz" element={<DeodorantQuiz />} />
            <Route path="/deodorant-quiz" element={<DeodorantQuiz />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
